import { useTranslation } from "react-i18next";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export function parseMessageDate(timestamp: unknown): Date | null {
  if (typeof timestamp !== "string" || !timestamp.trim()) return null;
  const date = new Date(timestamp);
  return Number.isFinite(date.getTime()) ? date : null;
}

export function formatPreciseDate(date: Date, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    timeZoneName: "short",
  }).format(date);
}

/** Shared aligned rows for the user and assistant metadata surfaces. */
export function MessageMetaRows({
  rows,
}: {
  rows: { label: string; value: string; tabular?: boolean }[];
}) {
  return (
    <dl className="grid grid-cols-[6.5rem_minmax(0,1fr)] gap-x-4 gap-y-2.5 text-xs leading-5">
      {rows.map(({ label, value, tabular }) => (
        <div className="contents" key={label}>
          <dt className="text-muted-foreground">{label}</dt>
          <dd className={`min-w-0 break-words text-right font-medium ${tabular ? "tabular-nums" : ""}`}>
            {value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

export function AssistantMessageTimestamp({ timestamp }: { timestamp: unknown }) {
  const { t, i18n } = useTranslation();
  const date = parseMessageDate(timestamp);
  if (!date) return null;

  const locale = i18n.resolvedLanguage || i18n.language;
  const now = new Date();
  const day = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1).getTime();
  const dayLabel = day === today
    ? t("messageMeta.today")
    : day === yesterday
      ? t("messageMeta.yesterday")
      : new Intl.DateTimeFormat(locale, {
          month: "short",
          day: "numeric",
          ...(date.getFullYear() !== now.getFullYear() ? { year: "numeric" } : {}),
        }).format(date);
  const time = new Intl.DateTimeFormat(locale, {
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
  const precise = formatPreciseDate(date, locale);
  const fullDate = new Intl.DateTimeFormat(locale, {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(date);
  const fullTime = new Intl.DateTimeFormat(locale, {
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    timeZoneName: "short",
  }).format(date);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex h-5 items-center rounded-sm text-[11px] tabular-nums text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={t("messageMeta.showTimestamp", { timestamp: precise })}
          data-testid="assistant-message-timestamp"
        >
          {dayLabel} · {time}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={6} className="w-72 max-w-[calc(100vw-2rem)] p-4">
        <MessageMetaRows rows={[
          { label: t("messageMeta.date"), value: fullDate },
          { label: t("messageMeta.time"), value: fullTime, tabular: true },
        ]} />
      </PopoverContent>
    </Popover>
  );
}
